import Dexie, { type EntityTable } from 'dexie'
import type { SessionUser, TeamChoice } from '@ghp/domain'

/**
 * La session persistée localement (D22 pt.1) : refresh + identité + équipe, en IndexedDB —
 * ni en mémoire, ni en `sessionStorage` — pour qu'un rechargement hors réseau ne renvoie
 * jamais l'agent à l'écran de connexion.
 *
 * `team` est un `TeamChoice` complet et pas seulement l'id : hors réseau, l'écran doit
 * pouvoir nommer l'équipe sans rien demander au serveur. `null` couvre l'agent sans équipe,
 * qui entre quand même (M6).
 */
export interface StoredSession {
  /** Une tablette ne porte qu'une session : la clé est fixe, l'écriture écrase. */
  id: 'current'
  refreshToken: string
  user: SessionUser
  team: TeamChoice | null
}

type SessionDatabase = Dexie & { session: EntityTable<StoredSession, 'id'> }

/**
 * ⚠️ Base dédiée à la session, distincte de celles des tournées (`ghp-terrain-data-<équipe>`) : leurs
 * cycles de vie diffèrent (une déconnexion volontaire efface la session, jamais les saisies —
 * D22).
 *
 * ⚠️ Cette séparation n'est **pas** ce qui protège l'équipe suivante : effacer la session ne
 * retire rien des bases de tournée. Ce qui la protège est **une base par équipe** (D22 pt.4) :
 * les saisies de l'équipe précédente restent dans la sienne, qui n'est pas ouverte.
 *
 * Le nom porte donc son contenu : `ghp-terrain` tout court reste libre. Renommer une base
 * IndexedDB déjà déployée demanderait de l'ouvrir, de la recopier puis de la supprimer — D22
 * interdit l'effacement pur et simple.
 */
export function openSessionDatabase(): SessionDatabase {
  const db = new Dexie('ghp-terrain-session') as SessionDatabase
  db.version(1).stores({ session: 'id' })
  return db
}

export async function saveSession(
  db: SessionDatabase,
  session: Omit<StoredSession, 'id'>,
): Promise<void> {
  await db.session.put({ id: 'current', ...session })
}

/**
 * Réécrit la session **seulement si la base porte encore ce jeton de rafraîchissement**, et rend
 * `false` sans rien écrire sinon.
 *
 * Pour qui a lu la session **avant** un appel réseau et veut la corriger après : entre les deux,
 * l'agent a pu se déconnecter, ou un autre se connecter. Réécrire sans regarder rouvrirait au
 * prochain démarrage la session qu'on vient de quitter, ou remplacerait celle de l'agent qui vient
 * d'entrer.
 *
 * ⚠️ **Une seule opération**, un `modify` filtré : la comparaison et l'écriture se font dans la
 * même transaction, et aucune connexion ne passe entre les deux. Un `get` suivi d'un `put` en
 * laisserait passer une.
 */
export async function replaceSessionIfUnchanged(
  db: SessionDatabase,
  expectedRefreshToken: string,
  session: Omit<StoredSession, 'id'>,
): Promise<boolean> {
  const replaced = await db.session
    .where('id')
    .equals('current')
    .and((stored) => stored.refreshToken === expectedRefreshToken)
    .modify({ ...session })

  return replaced > 0
}

export function loadSession(db: SessionDatabase): Promise<StoredSession | undefined> {
  return db.session.get('current')
}

/**
 * Réservé à la déconnexion volontaire : un échec de refresh ne passe jamais par ici (D22).
 *
 * ⚠️ Efface **cette** base et rien d'autre. Ni demande, ni brouillon, ni photo, ni envoi en
 * file ne bougent : ce sont les lectures qui cloisonnent (D22 pt.4).
 */
export async function clearSession(db: SessionDatabase): Promise<void> {
  await db.session.delete('current')
}
